import { DEFAULT_TIMEOUT, DEFAULT_OAUTH_THRESHOLD } from './constants.js';
export { DEFAULT_TIMEOUT } from './constants.js';

/*
 * @forgerock/javascript-sdk
 *
 * index.ts
 *
 * Copyright (c) 2020 ForgeRock. All rights reserved.
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
/**
 * Sets defaults for options that are required but have no supplied value
 * @param options The options to set defaults for
 * @returns options The options with defaults
 */
function setDefaults(options) {
    return {
        ...options,
        oauthThreshold: options.oauthThreshold || DEFAULT_OAUTH_THRESHOLD,
        logLevel: options.logLevel || 'none',
    };
}
/**
 * Utility for merging configuration defaults with one-off options.
 *
 * Example:
 *
 * ```js
 * // Establish configuration defaults
 * Config.set({
 *   clientId: 'myApp',
 *   serverConfig: { baseUrl: 'https://openam-domain.com/am' },
 *   tree: 'UsernamePassword'
 * });
 *
 * // Specify overrides as needed
 * const configOverrides = { tree: 'PasswordlessWebAuthn' };
 * const step = await FRAuth.next(undefined, configOverrides);
 */
class Config {
    /**
     * Sets the default options.
     *
     * @param options The options to use as defaults
     */
    static set(options) {
        if (!this.isValid(options)) {
            throw new Error('Configuration is invalid');
        }
        if (options.serverConfig) {
            this.validateServerConfig(options.serverConfig);
        }
        this.options = {
            ...setDefaults(options),
        };
    }
    /**
     * Merges the provided options with the default options.  Ensures a server configuration exists.
     *
     * @param options The options to merge with defaults
     */
    static get(options) {
        if (!this.options && !options) {
            throw new Error('Configuration has not been set');
        }
        const merged = { ...this.options, ...options };
        if (!merged.serverConfig || !merged.serverConfig.baseUrl) {
            throw new Error('Server configuration has not been set');
        }
        return merged;
    }
    static isValid(options) {
        return !!(options && options.serverConfig);
    }
    static validateServerConfig(serverConfig) {
        if (!serverConfig.timeout) {
            serverConfig.timeout = DEFAULT_TIMEOUT;
        }
        const url = serverConfig.baseUrl;
        if (url && url.charAt(url.length - 1) !== '/') {
            serverConfig.baseUrl = url + '/';
        }
    }
}

export { Config as default };
